import { Component, HostListener, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ClinicService } from '../../networking/services/clinic.service';
import { ContentBlockService } from '../../networking/services/content-block.service';
import { AuthService } from '../../helper/services/auth.service';
import { ChangeTrackerService } from '../../helper/services/change-tracker.service';
import { NotificationService } from '../../helper/services/notification.service';
import { CONSTANTS } from '../../helper/constants';
import { Clinic } from '../../data/models/Clinic';
import { ClinicContentBlock } from '../../data/models/ClinicContentBlock';
import { ClinicContentBlockType } from '../../data/models/ClinicContentBlockType';
import { HasUnsavedChanges } from '../../helper/services/unsaved-changes.guard';
import { BottomBarComponent } from '../components/bottom-bar/bottom-bar.component';
import { BlockEditorComponent } from '../components/block-editor/block-editor.component';
import { PreviewComponent } from '../preview/preview.component';

@Component({
  selector: 'app-portal',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    BottomBarComponent,
    BlockEditorComponent,
    PreviewComponent
  ],
  templateUrl: './portal.component.html',
  styleUrl: './portal.component.scss'
})
export class PortalComponent implements OnInit, HasUnsavedChanges {
  clinic: Clinic | null = null
  blocks: ClinicContentBlock[] = []
  blockTypes: ClinicContentBlockType[] = []
  isLoading = true
  isSaving = false

  constructor(
    private clinicService: ClinicService,
    private contentBlockService: ContentBlockService,
    private authService: AuthService,
    private changeTrackerService: ChangeTrackerService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadData()
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault()
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault()
      if (this.hasUnsavedChanges() && !this.isSaving) {
        this.saveAll()
      }
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true

    try {
      const blockTypesResponse = await new Promise<{ content_block_types: ClinicContentBlockType[] }>((resolve, reject) => {
        this.contentBlockService.getBlockTypes().subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        })
      })
      this.blockTypes = blockTypesResponse.content_block_types

      const clinic = await new Promise<Clinic>((resolve, reject) => {
        this.clinicService.getClinicDetails().subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        })
      })
      this.clinic = clinic
      this.blocks = (clinic.content_blocks || []).sort((a, b) => a.order - b.order)
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error)
    } finally {
      this.isLoading = false
    }
  }

  hasUnsavedChanges(): boolean {
    return this.changeTrackerService.hasChanges()
  }

  async saveAll(): Promise<void> {
    this.isSaving = true
    try {
      await this.changeTrackerService.applyAll(this.clinicService, this.contentBlockService)
      await this.loadData()
      this.notificationService.showSuccess(CONSTANTS.saveSuccessMessage)
    } catch (error) {
      this.notificationService.showError(CONSTANTS.saveErrorMessage)
      await this.loadData()
    } finally {
      this.isSaving = false
    }
  }
}
